import { Alert, Box, Button, Collapse } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivateDeviceDocument, useActivateDevice } from '~/api/devices';
import { useNotifications } from '~/api/helpers';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { CodeInput, validateCode } from '../../components';

export interface ReplaceDialogProps {
  deviceId: number;
  networkId: number;
  onClose: () => void;
  open: boolean;
}

export const ReplaceDialog = ({ deviceId, networkId, onClose, open }: ReplaceDialogProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState<string>();
  const [activate, { error, loading, reset }] = useActivateDevice(
    useNotifications(ActivateDeviceDocument, {
      success: t('api.devices.update_success'),
    }),
  );

  const handleClose = () => {
    setCode(undefined);
    reset();
    onClose();
  };

  const validCode = validateCode(code ?? null);

  const handleSubmit = async () => {
    if (validCode == null) return;
    const result = await activate({
      variables: { input: { code: validCode, deviceId, networkId } },
    });
    if (result.data?.activateDevice?.device != null) handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>{t('pages.devices.show.menu.replace.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.devices.show.menu.replace.prompt')}</DialogContentTitle>

        <Collapse in={error != null}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message ?? t('pages.devices.show.menu.replace.error')}
          </Alert>
        </Collapse>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CodeInput code={code} onChange={setCode} />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={validCode == null || loading}
          onClick={handleSubmit}
          variant="contained"
        >
          {t('pages.devices.show.menu.replace.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
